import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import HTMLParser from 'html-react-parser';
import { useClientTranslation } from '@/lib/core/i18n';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useExploreVenueDetail } from '@/services/platform/data/explore';
import { getSevenRoomsSeatingAreas } from '@/services/platform/data/booking/sevenrooms';
import type { SeatingAreaDto, VenueGroupVenueDto } from '@/services/platform/api/model';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { NotFoundError } from '@/components/shared/errors';
import { ScreenHead } from '@/components/shared/screen/head';
import { Icon } from '@/components/ui/icon';
import { shareContent, toShareUrl } from '@/lib/core/utils/share';
import { AssistanceBlock } from '../ui/assistance-block';
import { CoveringFooter, coveredPanelClassName, useCoveredBottomPanel } from '../ui/covering-footer';
import { useVenueBookmarks } from '../ui/useVenueBookmarks';
import { asDetailBlock, type VenueDetailBlock } from './types';
import { HeroBanner } from './hero-banner';
import { IntroBlock } from './intro-block';
import { OpeningHoursBlock } from './opening-hours-block';
import { AccordionBlock, type AccordionBlockItem } from './accordion-block';
import { VenueSliderBlock } from './venue-slider-block';
import { RoomsCarouselBlock, type RoomCarouselItem } from './rooms-carousel-block';
import { EventSliderBlock } from './event-slider-block';
import { ShopSliderBlock } from './shop-slider-block';
import { BottomCtaBar } from './bottom-cta-bar';
import { useApp } from '@/lib/core/providers/appProvider';

interface VenueDetailLocationState {
  venue?: VenueGroupVenueDto;
  venueGroupName?: string | null;
}

type AccordionGroupBlock = { type: 'accordionGroup'; items: AccordionBlockItem[] };
type GroupedDetailBlock = VenueDetailBlock | AccordionGroupBlock;

function slugify(value?: string | null): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function groupAccordionBlocks(blocks: (VenueDetailBlock | null)[]): GroupedDetailBlock[] {
  const grouped: GroupedDetailBlock[] = [];
  for (const block of blocks) {
    if (!block) continue;
    if (block.type === 'accordion') {
      const last = grouped[grouped.length - 1];
      if (last && last.type === 'accordionGroup') {
        last.items.push({ title: block.title, description: block.description });
      } else {
        grouped.push({
          type: 'accordionGroup',
          items: [{ title: block.title, description: block.description }],
        });
      }
    } else {
      grouped.push(block);
    }
  }
  return grouped;
}

interface RenderBlockContext {
  venueSalesforceId?: string | null;
  seatingAreas: SeatingAreaDto[];
}

function renderDetailBlock(block: GroupedDetailBlock, i: number, ctx: RenderBlockContext) {
  switch (block.type) {
    case 'intro':
      return null;
    case 'openingHours':
      return <OpeningHoursBlock key={i} items={block.items} />;
    case 'accordionGroup':
      return <AccordionBlock key={i} items={block.items} />;
    case 'venueSlider':
      return (
        <VenueSliderBlock key={i} title={block.title} titleHeading={block.titleHeading} items={block.items} />
      );
    case 'eventSlider':
      return (
        <EventSliderBlock
          key={i}
          title={block.title}
          titleHeading={block.titleHeading}
          link={block.link}
          venueSalesforceId={ctx.venueSalesforceId}
        />
      );
    case 'shopSlider':
      return (
        <ShopSliderBlock key={i} title={block.title} titleHeading={block.titleHeading} items={block.items} />
      );
    case 'roomsSlider': {
      // A room only renders when it has both live SevenRooms availability and a matching
      // CMS item — matched by slug, since seating areas carry no CMS content of their own.
      const rooms: RoomCarouselItem[] = ctx.seatingAreas.flatMap(area => {
        const item = block.items?.find(i => slugify(i.slug) === slugify(area.name));
        return item ? [{ area, item }] : [];
      });
      return (
        <RoomsCarouselBlock key={i} title={block.title} titleHeading={block.titleHeading} rooms={rooms} />
      );
    }
    default:
      return null;
  }
}

export function VenueDetailScreen() {
  const { '*': venueNodeKey = '' } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: VenueDetailLocationState | null };
  const { user } = useApp();
  const t = useClientTranslation();
  const setMainClassName = useUIStore(s => s.setMainClassName);
  const [seatingAreas, setSeatingAreas] = useState<SeatingAreaDto[]>([]);
  const { isBookmarked, toggleBookmark } = useVenueBookmarks();
  const isMember = !!user?.hasActiveMembership;

  const { venue, isLoading } = useExploreVenueDetail(venueNodeKey);

  useEffect(() => {
    if (!venue?.nodeKey) return;
    let cancelled = false;
    getSevenRoomsSeatingAreas(venue.nodeKey).then(areas => {
      if (!cancelled) setSeatingAreas(areas ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [venue?.nodeKey]);

  useEffect(() => {
    setMainClassName('pb-0');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const { panelRef, footerRef, panelHeight, isPanelCovered } = useCoveredBottomPanel();

  if (isLoading) return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  if (!venue) return <NotFoundError />;

  const blocks = (venue.detail ?? []).map(asDetailBlock).filter(Boolean);
  const groupedBlocks = groupAccordionBlocks(blocks);
  const intro = blocks.find(b => b?.type === 'intro');
  const introData = intro?.type === 'intro' ? intro : null;
  const serviceCategoryName = state?.venue?.serviceCategoryName;
  const serviceCategoryNodeKey = venue.serviceCategoryNodeKey ?? state?.venue?.serviceCategoryNodeKey;
  const serviceCategorySlug = state?.venue?.serviceCategorySlug ?? venue.serviceCategorySlug;
  const isShop = state?.venue?.isFashionClinicStore ?? venue.isFashionClinicStore;
  const isExperience = serviceCategorySlug === 'experiences' || serviceCategorySlug === 'event';
  const isMembersOnlyLocked = !!venue.membersOnly && !isMember;
  const renderBlockContext: RenderBlockContext = {
    venueSalesforceId: venue.salesforceId,
    seatingAreas,
  };

  return (
    <div className="bg-jncquoi-neutral-00 flex min-h-svh flex-col">
      <div className="to-jncquoi-neutral-1000 fixed top-0 right-0 left-0 z-50 bg-gradient-to-t from-transparent from-0% to-[81.25%] backdrop-blur-[2.5px]">
        <ScreenHead
          title=""
          variant="overlay"
          onBack={() => navigate(-1)}
          ariaLabel={t('App.Labels.GoBack')}
          className="pt-safe-area"
          rightActions={
            <button
              type="button"
              aria-label={t('App.Labels.Share')}
              className="flex size-11 items-center justify-center"
              onClick={() => shareContent({ title: venue.name ?? '', url: toShareUrl(window.location.href) })}
            >
              <Icon
                name="share-ios"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-00"
              />
            </button>
          }
        />
      </div>

      <HeroBanner
        images={(venue.images ?? []).map(img => img?.url)}
        logoUrl={venue.logo?.url}
        name={venue.name}
        variant={isExperience ? 'experience' : 'standard'}
        titleAccent={venue.titleAccent}
        bookingType={isExperience && venue.byAppointment ? t('App.Labels.ByAppointment') : undefined}
        venueGroupName={isExperience ? state?.venueGroupName : undefined}
      />

      <div className="flex flex-1 flex-col gap-6">
        {/* Intro: tags, title, address */}
        <IntroBlock
          title={introData?.title}
          titleHeading={introData?.titleHeading}
          membersOnly={venue.membersOnly}
          serviceCategoryName={serviceCategoryName}
          address={introData?.address}
          isBookmarked={isBookmarked(venue)}
          onBookmark={() => toggleBookmark(venue)}
        />

        {/* Editorial description */}
        {introData?.description && (
          <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 px-4 text-[16px] leading-[19px] tracking-[0.25px]">
            {HTMLParser(introData.description)}
          </div>
        )}

        {/* Detail blocks, in CMS order */}
        {groupedBlocks.map((block, i) => renderDetailBlock(block, i, renderBlockContext))}
      </div>

      <CoveringFooter footerRef={footerRef} panelHeight={panelHeight} isPanelCovered={isPanelCovered}>
        <AssistanceBlock />
      </CoveringFooter>

      <BottomCtaBar
        ref={panelRef}
        className={coveredPanelClassName(isPanelCovered)}
        primaryCta={introData?.primaryCta}
        //primaryCtaDisabled={isMembersOnlyLocked}
        secondaryCta={introData?.secondaryCta}
        //secondaryCtaDisabled={isMembersOnlyLocked}
        serviceCategoryNodeKey={serviceCategoryNodeKey}
        venueNodeKey={venue.nodeKey}
        isShop={isShop}
        bookingDisabled={isMembersOnlyLocked}
      />
    </div>
  );
}
